/**
 * useBudgets Hook
 *
 * 예산(Budget) 데이터 로딩을 위한 React Hook
 */

import { useState, useEffect } from 'react'
import { budgetRepository } from '../repositories'
import type { Budget } from '../types/Budget'
import type { BudgetFilter } from '../repositories/BudgetRepository'

/**
 * useBudgets Hook 반환 타입
 */
export interface UseBudgetsReturn {
  /** 예산 목록 */
  budgets: Budget[]
  /** 로딩 상태 */
  loading: boolean
  /** 에러 정보 */
  error: Error | null
  /** 데이터 재조회 함수 */
  refetch: () => void
}

/**
 * 예산 데이터를 로드하는 Hook
 *
 * @param filter 조회 필터 (선택)
 * @returns 예산 목록, 로딩 상태, 에러, 재조회 함수
 *
 * @example
 * ```typescript
 * // 2025년 12월 활성 예산 조회
 * const { budgets, loading, error } = useBudgets({
 *   year: 2025,
 *   month: 12,
 *   isActive: true
 * })
 * ```
 */
export function useBudgets(filter?: BudgetFilter): UseBudgetsReturn {
  const [budgets, setBudgets] = useState<Budget[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)

  /**
   * Repository에서 예산 데이터 조회
   */
  const fetchBudgets = async () => {
    try {
      setLoading(true)
      setError(null)
      const data = await budgetRepository.findAll(filter)
      setBudgets(data)
    } catch (err) {
      setError(err instanceof Error ? err : new Error('예산 목록을 불러오는데 실패했습니다'))
    } finally {
      setLoading(false)
    }
  }

  /**
   * 필터 변경 시 자동으로 재조회
   *
   * @why 이전엔 JSON.stringify(filter)로 dep을 만들었지만, 매 렌더 시
   * 직렬화 비용 + linter 경고가 있어 useTransactions와 동일하게
   * 개별 필드를 의존성에 명시하는 방식으로 통일함.
   */
  useEffect(() => {
    fetchBudgets()
  }, [
    filter?.year,
    filter?.month,
    filter?.categoryId,
    filter?.parentBudgetId,
    filter?.isActive,
  ])

  return {
    budgets,
    loading,
    error,
    refetch: fetchBudgets,
  }
}
